import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Sidebar, Spinner } from './components/UI';
import { StudentSidebar } from './students/StudentApp';
import LandingPage from './pages/LandingPage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import DashboardPage from './pages/DashboardPage';
import LeadsPage from './pages/LeadsPage';
import TemplatesPage from './pages/TemplatesPage';
import SendPage from './pages/SendPage';
import SettingsPage from './pages/SettingsPage';
import CVsPage from './students/pages/CVsPage';
import CoverLettersPage from './students/pages/CoverLettersPage';
import EmailBodiesPage from './students/pages/EmailBodiesPage';
import ApplyPage from './students/pages/ApplyPage';
import StudentSettingsPage from './students/pages/StudentSettingsPage';
import CompaniesPage from './students/pages/CompaniesPage';
import FindJobsPage from './students/pages/FindJobsPage';
import AIApplicationPage from './students/pages/AIApplicationPage';

function ProtectedLayout({ children }: { children: React.ReactNode }): React.ReactElement {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--accent)' }}>
      <Spinner size={32} />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="app-layout">
      <Sidebar />
      <main className="app-main">{children}</main>
    </div>
  );
}

function StudentLayout({ children }: { children: React.ReactNode }): React.ReactElement {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--accent)' }}><Spinner size={32} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="app-layout">
      <StudentSidebar />
      <main className="app-main">{children}</main>
    </div>
  );
}

function GuestRoute({ children }: { children: React.ReactElement }): React.ReactElement {
  const { user, loading } = useAuth();
  if (loading) return <></>;
  if (user) return <Navigate to="/app/dashboard" replace />;
  return children;
}

export default function App(): React.ReactElement {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/"              element={<LandingPage />} />
          <Route path="/login"         element={<GuestRoute><LoginPage /></GuestRoute>} />
          <Route path="/register"      element={<GuestRoute><RegisterPage /></GuestRoute>} />
          <Route path="/app/dashboard" element={<ProtectedLayout><DashboardPage /></ProtectedLayout>} />
          <Route path="/app/leads"     element={<ProtectedLayout><LeadsPage /></ProtectedLayout>} />
          <Route path="/app/templates" element={<ProtectedLayout><TemplatesPage /></ProtectedLayout>} />
          <Route path="/app/send"      element={<ProtectedLayout><SendPage /></ProtectedLayout>} />
          <Route path="/app/settings"  element={<ProtectedLayout><SettingsPage /></ProtectedLayout>} />
          <Route path="/app"           element={<Navigate to="/app/dashboard" replace />} />
          {/* Student Module */}
          <Route path="/student/cvs"           element={<StudentLayout><CVsPage /></StudentLayout>} />
          <Route path="/student/coverletters"  element={<StudentLayout><CoverLettersPage /></StudentLayout>} />
          <Route path="/student/emailbodies"   element={<StudentLayout><EmailBodiesPage /></StudentLayout>} />
          <Route path="/student/companies"     element={<StudentLayout><CompaniesPage /></StudentLayout>} />
          <Route path="/student/find-jobs"     element={<StudentLayout><FindJobsPage /></StudentLayout>} />
          <Route path="/student/ai-application" element={<StudentLayout><AIApplicationPage /></StudentLayout>} />
          <Route path="/student/apply"         element={<StudentLayout><ApplyPage /></StudentLayout>} />
          <Route path="/student/settings"      element={<StudentLayout><StudentSettingsPage /></StudentLayout>} />
          <Route path="/student"               element={<Navigate to="/student/cvs" replace />} />
          <Route path="*"              element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
